/**
 * Pure procedural generator — a delicate sapling, matching the hand-drawn
 * reference:
 *
 *   thick trunk from the ground → a few thinner branches that fork off
 *   alternately and taper to fine points → a single small leaf at each
 *   branch tip (sometimes a tiny pair, rarely one more partway). The
 *   trunk itself is bare; leaves are sparse, never a bushy cluster.
 *
 * Design rules from the reference sketch:
 *   - "grows upward from bottom"     → trunk rooted below the viewport
 *   - "thicker stem than branches"   → trunk base wide, branches slim
 *   - "tapered smooth edges"         → limbs narrow to near-points
 *   - "perch on any branch, not the  → perch candidates live only on the
 *      main tree or a leaf"             bare INNER half of branches
 *
 * The trunk and branches are filled tapered ribbons (smooth); each leaf
 * is a small hand-shaped sprig placed by transform (see `foliageAsset`).
 * Branches keep their inner half bare both so the bird has a clear place
 * to perch and so leaves read as growing from the twig ends.
 *
 * Everything is in viewport coordinates (the overlay is `position:
 * fixed`). Pure and seeded, so the same seed reproduces the same tree.
 */

import { createRng, type Rng } from "./rng";
import { FOLIAGE_ANCHOR, FOLIAGE_AXIS_DEG, FOLIAGE_AXIS_LEN } from "./foliageAsset";

export interface LimbPart {
  /** Filled tapered-ribbon outline of the trunk or a branch. */
  d: string;
  order: number;
}
export interface FoliagePart {
  /** SVG transform that places a leaf instance. */
  transform: string;
  order: number;
}
export interface PerchPoint {
  viewportX: number;
  viewportY: number;
}
export interface FieldGeometry {
  limbs: LimbPart[];
  foliage: FoliagePart[];
  perchPoints: PerchPoint[];
  maxOrder: number;
}
export interface GenerateFieldOptions {
  width: number;
  height: number;
  seed: number;
  marginWidth: number;
}

// --- Tuning -----------------------------------------------------------

const STEP_PX = 8;

const TRUNK_BASE_WIDTH_MIN = 22;
const TRUNK_BASE_WIDTH_MAX = 28;
// Trunk keeps some body up top (doesn't taper to a thread), so branches
// always read as thinner than the stem they grow from.
const TRUNK_TIP_WIDTH = 5;
// Upright overall, but the stem snakes like a vine as it climbs — a
// gentle multi-wave S-curve rather than a stiff pole.
const TRUNK_LEAN_MIN_DEG = 86;
const TRUNK_LEAN_MAX_DEG = 90;
const TRUNK_BOW = 0.06;
const TRUNK_WAVES = 1.7;
const TRUNK_LENGTH_FRAC_MIN = 0.7;
const TRUNK_LENGTH_FRAC_MAX = 0.86;

// A few branches alternating up the stem. Each leaves the trunk steeply
// then arcs over toward horizontal (vine-like drape), giving a flat
// stretch to perch on and a graceful curve to hang leaves along.
const BRANCH_COUNT_MIN = 4;
const BRANCH_COUNT_MAX = 5;
// Thick body, but taper to a fine point — a blunt tip reads as a snapped
// club, not a twig.
const BRANCH_TIP_WIDTH = 1;
const BRANCH_ANGLE_UP_MIN_DEG = 46;
const BRANCH_ANGLE_UP_MAX_DEG = 64;
// How far toward horizontal the branch turns (1 = fully level), and how
// early in its length it gets there. Levelling by ~0.6 leaves a genuine
// horizontal drape over the outer half — a flat perch and a vine line.
const BRANCH_ARC_FRAC = 0.9;
const BRANCH_ARC_COMPLETE_T = 0.6;
const BRANCH_WAVE_AMP_DEG = 4;
const BRANCH_LEN_MIN = 100;
const BRANCH_LEN_MAX = 205;
// Tuck the branch base a little way down INTO the trunk so its flat cap
// is hidden in the wood and the junction reads as a smooth fork.
const BRANCH_OVERLAP_PX = 7;

// Leaf sizing, as a fraction of the leaf asset's natural axis length.
// Big leaves on slim branches — the money-plant look.
const LEAF_SCALE_MIN = 1.2;
const LEAF_SCALE_MAX = 1.55;
// Leaves run ALONG the branch — kept off the fork (where they'd overlap
// the trunk) but carried right out near the tip so the branch ends in
// foliage, not a bare twig.
const LEAF_ALONG_FROM_T = 0.28;
// Stop short of the very tip: a leaf ON the endpoint has no branch past
// it and reads as a terminal bud/blob. Leaving a short tapered tip past
// the last leaf keeps every leaf a proper SIDE leaf.
const LEAF_ALONG_TO_T = 0.8;
const LEAF_ALONG_SPACING_PX = 20;
const LEAF_MAX_PER_BRANCH = 8;
// Don't hang a leaf where the branch is thinner than its own stem — that
// looked like a leaf stuck on a thread near the tip. Leaves are scaled
// to the local branch width (see leafAlong), so they sit in proportion
// and the branch ends IN foliage instead of a bare prong.
const LEAF_MIN_BRANCH_WIDTH = 1.5;
const LEAF_WIDTH_REF_PX = 6.5;
// Each leaf reaches up-AND-outward, trailing along the branch's run like
// a vine, tilted alternately left/right — but never drooping below
// horizontal (the old perpendicular lean let half of them point down).
const LEAF_OUT_LEAN = 0.55;
const LEAF_TILT_MIN_DEG = 8;
const LEAF_TILT_MAX_DEG = 24;
const LEAF_ANGLE_JITTER_DEG = 7;

// The bird's feet sit at the bottom of its 96px sprite, so its center
// (where the perch target is placed) must ride this far above the branch
// for the feet to land on the wood.
const SPRITE_FOOT_OFFSET_PX = 44;
const PERCH_MIN_WIDTH = 2.5;
const PERCH_MIN_GAP_PX = 110;
const MAX_PERCHES_PER_SIDE = 3;
const PERCH_EDGE_MARGIN_PX = 70;
// Extra clearance along the BOTTOM edge so the bird never nests down in
// the corner where the always-on "KOKO" status pill sits (bottom-right) —
// otherwise it perches half-hidden behind the pill. Pushes resting spots
// up onto clearly visible branches.
const PERCH_BOTTOM_MARGIN_PX = 180;
// Keep the (96px) bird clear of the viewport's left/right edges so it
// never perches half-clipped on a branch's extreme tip.
const PERCH_EDGE_H_PX = 80;
// Keep just the bird's footing clear of a leaf. Small on purpose: every
// branch carries a perch candidate, so a large radius would strip leaves
// from the middle of every branch. The bird is 96px wide, so a leaf this
// far to the side sits beside it, not under its feet.
const PERCH_CLEAR_PX = 16;

const DEG = Math.PI / 180;

interface Vec {
  x: number;
  y: number;
}
interface Limb {
  center: Vec[];
  widths: number[];
  tip: Vec;
}
interface PerchCandidate {
  x: number;
  y: number;
  flatness: number;
  side: number;
}
interface LeafFootprint {
  x: number;
  y: number;
  r: number;
}
interface FieldContext {
  rng: Rng;
  width: number;
  height: number;
  limbs: LimbPart[];
  foliage: FoliagePart[];
  perchCandidates: PerchCandidate[];
  /** Circles approximating every placed leaf, so new leaves can avoid
   *  intersecting them (shared across both trees). */
  leafFootprints: LeafFootprint[];
  order: number;
  inwardSign: number;
  edgeX: number;
  maxInward: number;
}

function norm(v: Vec): Vec {
  const m = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / m, y: v.y / m };
}
function num(value: number): string {
  return value.toFixed(1);
}
/** Screen-space angle of a vector, in degrees (atan2 convention). */
function degOf(v: Vec): number {
  return Math.atan2(v.y, v.x) / DEG;
}
function quadThrough(points: Vec[]): string {
  let d = "";
  for (let i = 1; i < points.length - 1; i++) {
    const mx = (points[i].x + points[i + 1].x) / 2;
    const my = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${num(points[i].x)} ${num(points[i].y)} ${num(mx)} ${num(my)}`;
  }
  const last = points[points.length - 1];
  d += ` L ${num(last.x)} ${num(last.y)}`;
  return d;
}
function smoothOpen(points: Vec[]): string {
  return `M ${num(points[0].x)} ${num(points[0].y)}${quadThrough(points)}`;
}

/** Filled tapered ribbon around a centerline (smooth edges). */
function ribbonPath(center: Vec[], widths: number[]): string {
  const n = center.length;
  const left: Vec[] = [];
  const right: Vec[] = [];
  for (let i = 0; i < n; i++) {
    const prev = center[Math.max(0, i - 1)];
    const next = center[Math.min(n - 1, i + 1)];
    const tangent = norm({ x: next.x - prev.x, y: next.y - prev.y });
    const normal = { x: -tangent.y, y: tangent.x };
    const half = widths[i] / 2;
    left.push({ x: center[i].x + normal.x * half, y: center[i].y + normal.y * half });
    right.push({ x: center[i].x - normal.x * half, y: center[i].y - normal.y * half });
  }
  // Round the tip into a clean point: curve from the left edge through a
  // forward apex to the right edge, instead of a flat cap (which looked
  // like a snapped-off club). Applies to trunk and branches alike.
  const tip = center[n - 1];
  const tipPrev = center[Math.max(0, n - 2)];
  const tipTan = norm({ x: tip.x - tipPrev.x, y: tip.y - tipPrev.y });
  const apex = {
    x: tip.x + tipTan.x * (widths[n - 1] / 2),
    y: tip.y + tipTan.y * (widths[n - 1] / 2),
  };
  return (
    smoothOpen(left) +
    ` Q ${num(apex.x)} ${num(apex.y)} ${num(right[n - 1].x)} ${num(right[n - 1].y)}` +
    quadThrough([...right].reverse()) +
    " Z"
  );
}

function clampX(ctx: FieldContext, x: number): number {
  // Cap how far inward a limb may reach (never cross into the centered
  // content), but allow short outward branches toward the edge — just
  // keep them on-screen.
  const inward = (x - ctx.edgeX) * ctx.inwardSign;
  const nx = ctx.edgeX + ctx.inwardSign * Math.min(ctx.maxInward, inward);
  return Math.max(4, Math.min(ctx.width - 4, nx));
}

function emit(ctx: FieldContext, center: Vec[], widths: number[]): Limb {
  ctx.limbs.push({ d: ribbonPath(center, widths), order: ctx.order++ });
  return { center, widths, tip: center[center.length - 1] };
}

/** Smooth tapered limb from `start` along `dir`, with a gentle bow. */
function buildLimb(
  ctx: FieldContext,
  start: Vec,
  dir: Vec,
  length: number,
  baseWidth: number,
  tipWidth: number,
  bow: number,
  waves = 1,
): Limb {
  const perp = { x: -dir.y, y: dir.x };
  const steps = Math.max(2, Math.floor(length / STEP_PX));
  const center: Vec[] = [];
  const widths: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const along = t * length;
    // `waves` > 1 turns the single hump into a snaking S-curve (vine).
    const bend = Math.sin(t * Math.PI * waves) * bow * length;
    center.push({
      x: clampX(ctx, start.x + dir.x * along + perp.x * bend),
      y: start.y + dir.y * along + perp.y * bend,
    });
    widths.push(widthAt(baseWidth, tipWidth, t));
  }
  return emit(ctx, center, widths);
}

/** Smooth ease — flat-ish in the middle, settling at both ends. */
function smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Limb width along the centerline. Holds most of its body through the
 * first 60% (a slow narrowing), then tapers to the fine tip over the last
 * 40% — so branches stay broad enough to carry leaves well out and end in
 * a clean point, with no long whippy thread past the foliage.
 */
function widthAt(baseWidth: number, tipWidth: number, t: number): number {
  const HOLD = 0.6;
  const bodyEnd = baseWidth * 0.62;
  if (t <= HOLD) {
    return baseWidth + (bodyEnd - baseWidth) * (t / HOLD);
  }
  return bodyEnd + (tipWidth - bodyEnd) * smootherstep((t - HOLD) / (1 - HOLD));
}

/**
 * A limb whose heading turns smoothly from `startDeg` by `turnDeg` over
 * its length (an arc), with an optional gentle wave. Used for branches
 * that leave the trunk steeply then drape toward horizontal — the curve
 * gives both a flat perch and a vine-like line to hang leaves along.
 */
function buildArcLimb(
  ctx: FieldContext,
  start: Vec,
  startDeg: number,
  turnDeg: number,
  length: number,
  baseWidth: number,
  tipWidth: number,
  waveAmpDeg: number,
  waveDir: number,
): Limb {
  const steps = Math.max(3, Math.floor(length / STEP_PX));
  const seg = length / steps;
  const center: Vec[] = [start];
  const widths: number[] = [baseWidth];
  let pos = start;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    // Most of the turn completes by `BRANCH_ARC_COMPLETE_T`, then the
    // heading holds ~level — a flat drape rather than a tip that keeps
    // curving down past horizontal.
    const turnT = Math.min(1, smootherstep(Math.min(1, t / BRANCH_ARC_COMPLETE_T)));
    const heading = startDeg + turnDeg * turnT + Math.sin(t * Math.PI) * waveAmpDeg * waveDir;
    const a = heading * DEG;
    pos = {
      x: clampX(ctx, pos.x + Math.cos(a) * seg),
      y: pos.y + Math.sin(a) * seg,
    };
    center.push(pos);
    widths.push(widthAt(baseWidth, tipWidth, t));
  }
  return emit(ctx, center, widths);
}

/**
 * Drop a single leaf at `at`, its stem axis pointed at `targetDeg`
 * (screen degrees) and scaled by `scale`. Implemented as the transform
 * that maps the leaf's anchor onto `at` and rotates/scales about it.
 */
function placeLeaf(
  ctx: FieldContext,
  at: Vec,
  targetDeg: number,
  scale: number,
  flip: boolean,
): void {
  // Mirroring (scale x by −1) flips the leaf's curve to the other side;
  // its base→tip axis mirrors to `-180 - FOLIAGE_AXIS_DEG`, so the
  // rotation that lands the axis on `targetDeg` changes accordingly.
  const axis = flip ? -180 - FOLIAGE_AXIS_DEG : FOLIAGE_AXIS_DEG;
  const rot = targetDeg - axis;
  const sx = (flip ? -scale : scale).toFixed(3);
  const transform =
    `translate(${num(at.x)} ${num(at.y)}) rotate(${rot.toFixed(1)}) ` +
    `scale(${sx} ${scale.toFixed(3)}) translate(${num(-FOLIAGE_ANCHOR.x)} ${num(-FOLIAGE_ANCHOR.y)})`;
  ctx.foliage.push({ transform, order: ctx.order++ });
}

/** Local tangent (direction of travel) at centerline index `i`. */
function tangentAt(limb: Limb, i: number): Vec {
  const n = limb.center.length;
  const a = limb.center[Math.max(0, i - 1)];
  const b = limb.center[Math.min(n - 1, i + 1)];
  return norm({ x: b.x - a.x, y: b.y - a.y });
}

/**
 * Direction a leaf grows. Start from "up" but lean along the branch's
 * outward run so leaves trail down the vine; clamp so they never point
 * below horizontal; then rotate by an alternating side tilt to fan them
 * left and right. Consistent and vine-like on any branch slope.
 */
function leafDir(branch: Limb, idx: number, side: number, rng: Rng): number {
  const out = tangentAt(branch, idx); // points along the branch toward its tip
  let dir = norm({ x: out.x * LEAF_OUT_LEAN, y: -1 + out.y * LEAF_OUT_LEAN });
  if (dir.y > -0.2) dir = norm({ x: dir.x, y: -0.45 }); // keep it above horizontal
  const a = side * rng.range(LEAF_TILT_MIN_DEG, LEAF_TILT_MAX_DEG) * DEG;
  const tilted = {
    x: dir.x * Math.cos(a) - dir.y * Math.sin(a),
    y: dir.x * Math.sin(a) + dir.y * Math.cos(a),
  };
  return degOf(tilted) + rng.range(-LEAF_ANGLE_JITTER_DEG, LEAF_ANGLE_JITTER_DEG);
}

/** Flattest, edge-safe, thick-enough point of a branch — or −1 if none. */
function findPerchIndex(ctx: FieldContext, branch: Limb): number {
  const bn = branch.center.length;
  let best = -1;
  let bestFlat = Infinity;
  for (let i = 1; i < bn - 1; i++) {
    const along = i / (bn - 1);
    if (along < 0.45 || along > 0.85) continue;
    const x = branch.center[i].x;
    if (x < PERCH_EDGE_H_PX || x > ctx.width - PERCH_EDGE_H_PX) continue;
    const flat = Math.abs(tangentAt(branch, i).y);
    if (branch.widths[i] >= PERCH_MIN_WIDTH && flat < bestFlat) {
      bestFlat = flat;
      best = i;
    }
  }
  return best;
}

/**
 * Hang curved leaves ALONG a branch, alternating sides, the way leaves
 * run down a vine. Spacing keeps them sparse; any leaf that would land on
 * the bird's perch (`avoid`) is skipped so the bird sits on bare wood.
 */
function leafAlong(ctx: FieldContext, branch: Limb, side0: number, avoid: Vec | null): void {
  const { rng } = ctx;
  const n = branch.center.length;
  const from = branch.center[Math.round(LEAF_ALONG_FROM_T * (n - 1))];
  const span = Math.hypot(branch.tip.x - from.x, branch.tip.y - from.y);
  const count = Math.max(2, Math.min(LEAF_MAX_PER_BRANCH, Math.round(span / LEAF_ALONG_SPACING_PX)));
  for (let k = 0; k < count; k++) {
    const t =
      LEAF_ALONG_FROM_T +
      (LEAF_ALONG_TO_T - LEAF_ALONG_FROM_T) * (count === 1 ? 0.9 : k / (count - 1));
    const idx = Math.min(n - 1, Math.max(1, Math.round(t * (n - 1))));
    const at = branch.center[idx];
    if (branch.widths[idx] < LEAF_MIN_BRANCH_WIDTH) continue;
    if (avoid && Math.hypot(at.x - avoid.x, at.y - avoid.y) < PERCH_CLEAR_PX) continue;
    const side = side0 * (k % 2 === 0 ? 1 : -1);
    // Gently taper leaves toward the thin tip, but keep them big overall
    // (money-plant leaves dwarf their stems), so foliage stays full.
    const wScale = Math.max(0.85, Math.min(1.1, branch.widths[idx] / LEAF_WIDTH_REF_PX));
    const scale = rng.range(LEAF_SCALE_MIN, LEAF_SCALE_MAX) * wScale;
    const dirDeg = leafDir(branch, idx, side, rng);
    // Skip a leaf that would intersect one already placed (anywhere).
    // Approximate each leaf as a circle over its blade's mid-span.
    const span = FOLIAGE_AXIS_LEN * scale;
    const r = span * 0.42;
    const cx = at.x + Math.cos(dirDeg * DEG) * span * 0.5;
    const cy = at.y + Math.sin(dirDeg * DEG) * span * 0.5;
    if (ctx.leafFootprints.some((f) => Math.hypot(f.x - cx, f.y - cy) < f.r + r)) continue;
    ctx.leafFootprints.push({ x: cx, y: cy, r });
    placeLeaf(ctx, at, dirDeg, scale, side < 0);
  }
}

function generateTree(ctx: FieldContext, marginWidth: number): void {
  const { rng, height } = ctx;
  const maxReach = ctx.maxInward;

  // --- Main trunk: near-vertical, rooted below the viewport. ---
  // Sit the stem toward the outer half of the margin so branches have
  // room to fork both inward and outward without leaving the screen.
  const baseX = ctx.edgeX + ctx.inwardSign * marginWidth * rng.range(0.46, 0.62);
  const baseY = height + 30;
  const trunkLength = rng.range(TRUNK_LENGTH_FRAC_MIN, TRUNK_LENGTH_FRAC_MAX) * height;
  const trunkBase = rng.range(TRUNK_BASE_WIDTH_MIN, TRUNK_BASE_WIDTH_MAX);
  const lean = rng.range(TRUNK_LEAN_MIN_DEG, TRUNK_LEAN_MAX_DEG);
  // Lean alternates direction so the gentle wiggle isn't always the same
  // way; nearly upright either way.
  const leanSign = rng.chance(0.5) ? 1 : -1;
  const trunkDir = {
    x: ctx.inwardSign * leanSign * Math.cos(lean * DEG),
    y: -Math.sin(lean * DEG),
  };
  const trunk = buildLimb(
    ctx,
    { x: baseX, y: baseY },
    trunkDir,
    trunkLength,
    trunkBase,
    TRUNK_TIP_WIDTH,
    TRUNK_BOW * (rng.chance(0.5) ? 1 : -1),
    TRUNK_WAVES,
  );

  // --- A few branches: leave the stem steeply, arc toward horizontal. ---
  const branchCount = rng.int(BRANCH_COUNT_MIN, BRANCH_COUNT_MAX);
  const tn = trunk.center.length;
  for (let b = 0; b < branchCount; b++) {
    // Spread attachment points up the lower-to-middle trunk, stopping
    // before the stem thins out — so a branch never grows from a point
    // thinner than itself, and the upper trunk stays bare above them.
    const t = 0.24 + (b + rng.range(0.15, 0.85)) * (0.46 / branchCount);
    const idx = Math.min(tn - 2, Math.max(1, Math.floor(t * (tn - 1))));
    const node = trunk.center[idx];
    const localWidth = trunk.widths[idx];

    // Alternate branches left/right of the trunk for a balanced fork.
    const fork = b % 2 === 0 ? 1 : -1;
    const angleUp = rng.range(BRANCH_ANGLE_UP_MIN_DEG, BRANCH_ANGLE_UP_MAX_DEG);
    const dir = {
      x: ctx.inwardSign * Math.cos(angleUp * DEG) * fork,
      y: -Math.sin(angleUp * DEG),
    };
    // Heading turns from steep-up toward the nearer horizontal, so the
    // branch arcs over like a vine and levels out a perch.
    const startDeg = degOf(dir);
    const horizonDeg = dir.x >= 0 ? 0 : -180;
    const turnDeg = (horizonDeg - startDeg) * BRANCH_ARC_FRAC;

    // Every branch gets a real length — never a stub. Floor it even after
    // the gentle "higher branches a bit shorter" falloff, and guard the
    // cap so a narrow margin can't invert the range.
    const lengthCap = Math.max(BRANCH_LEN_MIN + 25, Math.min(BRANCH_LEN_MAX, maxReach + 30));
    const length = Math.max(BRANCH_LEN_MIN, rng.range(BRANCH_LEN_MIN, lengthCap) * (1 - 0.16 * t));
    // Branch base: slim, and always a clear step thinner than the trunk
    // it grows from (never the weight inversion that looked wrong).
    const baseWidth = Math.max(3, Math.min(localWidth * 0.47, localWidth - 4));
    // Emerge from the trunk's EDGE on the branch's own side, tucked a
    // little way down the stem. Two things this avoids: (1) starting on
    // the centerline made the steep base run up THROUGH the trunk core,
    // piling into a swollen blob; (2) offsetting back along −branchDir
    // pushed the base out the FAR side. Now each branch leaves cleanly
    // from its side and stays there.
    const sideX = dir.x >= 0 ? 1 : -1;
    const trunkTan = tangentAt(trunk, idx);
    const start = {
      x: node.x + sideX * localWidth * 0.32 - trunkTan.x * BRANCH_OVERLAP_PX,
      y: node.y - trunkTan.y * BRANCH_OVERLAP_PX,
    };
    const branch = buildArcLimb(
      ctx,
      start,
      startDeg,
      turnDeg,
      length,
      baseWidth,
      BRANCH_TIP_WIDTH,
      BRANCH_WAVE_AMP_DEG,
      rng.chance(0.5) ? 1 : -1,
    );

    // Pick the perch first (flattest level stretch), then hang the
    // curved leaves along the branch — skipping the perch so the bird
    // never stands on a leaf.
    const perchIdx = findPerchIndex(ctx, branch);
    const perchPoint = perchIdx >= 0 ? branch.center[perchIdx] : null;
    leafAlong(ctx, branch, fork, perchPoint);

    if (perchIdx >= 0) {
      // Lift so the bird's feet — at the bottom of its sprite — land on
      // the wood.
      ctx.perchCandidates.push({
        x: branch.center[perchIdx].x,
        y: branch.center[perchIdx].y - branch.widths[perchIdx] / 2 - SPRITE_FOOT_OFFSET_PX,
        flatness: Math.abs(tangentAt(branch, perchIdx).y),
        side: ctx.inwardSign,
      });
    }
  }

  // The trunk stays bare — leaves belong to the branches only.
}

function selectPerchPoints(candidates: PerchCandidate[], height: number): PerchPoint[] {
  const chosen: PerchPoint[] = [];
  for (const sideSign of [-1, 1]) {
    const pool = candidates
      .filter(
        (c) =>
          c.side === sideSign &&
          c.y >= PERCH_EDGE_MARGIN_PX &&
          c.y <= height - PERCH_BOTTOM_MARGIN_PX,
      )
      .sort((a, b) => a.flatness - b.flatness);
    const picked: PerchPoint[] = [];
    for (const c of pool) {
      if (picked.length >= MAX_PERCHES_PER_SIDE) break;
      if (picked.some((p) => Math.abs(p.viewportY - c.y) < PERCH_MIN_GAP_PX)) continue;
      picked.push({ viewportX: c.x, viewportY: c.y });
    }
    chosen.push(...picked);
  }
  return chosen;
}

export function generateField(options: GenerateFieldOptions): FieldGeometry {
  const { width, height, seed, marginWidth } = options;
  const rng = createRng(seed);
  const shared = {
    rng,
    width,
    height,
    limbs: [] as LimbPart[],
    foliage: [] as FoliagePart[],
    perchCandidates: [] as PerchCandidate[],
    leafFootprints: [] as LeafFootprint[],
    order: 0,
    maxInward: Math.max(40, marginWidth - 28),
  };

  // Only the RIGHT-margin tree is drawn now — the left margin belongs to
  // the navigation sidebar. The bird therefore perches on the right only.
  const right: FieldContext = { ...shared, inwardSign: -1, edgeX: width };
  generateTree(right, marginWidth);

  return {
    limbs: right.limbs,
    foliage: right.foliage,
    perchPoints: selectPerchPoints(right.perchCandidates, height),
    maxOrder: right.order,
  };
}
